import * as Dialog from '@radix-ui/react-dialog';
import { FileText, Maximize2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvidenceFile } from '@/lib/api/ops-queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * The customer's raw uploaded slip — the file exactly as submitted, not the bank-attested
 * `VerifiedSlip` `SlipViewer` renders below it.
 *
 * `SlipViewer`'s own doc explains why PaymentFlow normally never shows this: the attested record
 * cannot be forged the way a photograph can. But that argument only holds when the two agree. A
 * `SENDER_MISMATCH`-class case is exactly the situation where they might not, and an operator
 * asked to rule on that has to be able to look at the actual file — otherwise the review screen is
 * asking for a judgment call on evidence nobody in the loop can see.
 *
 * The inline image is capped at the card's height, which is too small to read the fine print a
 * mismatch case turns on — so it doubles as the trigger for a full-screen preview.
 *
 * Renders nothing while loading, on error, and when there is no file to show (a QR-only
 * submission has no photograph at all) — an empty or perpetually-loading card would read as a
 * layout bug rather than as "there is nothing here to compare".
 */
export function CustomerUploadCard({
  transactionId,
}: {
  readonly transactionId: string;
}): ReactNode {
  const { t } = useTranslation();
  const file = useEvidenceFile(transactionId);

  if (!file.data) return null;

  const { url, contentType } = file.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('manualReview.slip.uploadTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contentType.startsWith('image/') ? (
          <Dialog.Root>
            <Dialog.Trigger asChild>
              <button
                type="button"
                aria-label={t('manualReview.slip.uploadPreviewOpen')}
                className="group relative block w-full cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <img
                  src={url}
                  alt={t('manualReview.slip.uploadTitle')}
                  className="max-h-96 w-full rounded-lg border border-border object-contain"
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100 group-focus-visible:bg-black/30 group-focus-visible:opacity-100">
                  <span className="flex items-center gap-2 rounded-md bg-background/90 px-3 py-1.5 text-xs font-medium">
                    <Maximize2 className="size-3.5" aria-hidden="true" />
                    {t('manualReview.slip.uploadPreviewOpen')}
                  </span>
                </span>
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 z-40 bg-black/80" />
              <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 focus:outline-none">
                <Dialog.Title className="sr-only">
                  {t('manualReview.slip.uploadTitle')}
                </Dialog.Title>
                <Dialog.Description className="sr-only">
                  {t('manualReview.slip.uploadPreviewDescription')}
                </Dialog.Description>
                <img
                  src={url}
                  alt={t('manualReview.slip.uploadTitle')}
                  className="max-h-[92vh] max-w-[92vw] rounded-lg object-contain"
                />
                <Dialog.Close asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('manualReview.slip.uploadPreviewClose')}
                    className="absolute right-2 top-2 bg-background/80 hover:bg-background"
                  >
                    <X aria-hidden="true" />
                  </Button>
                </Dialog.Close>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        ) : contentType === 'application/pdf' ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary underline underline-offset-2"
          >
            <FileText className="size-4" aria-hidden="true" />
            {t('manualReview.slip.uploadPdfLink')}
          </a>
        ) : (
          <p className="text-sm text-muted-foreground">{t('manualReview.slip.uploadNoPreview')}</p>
        )}
        <p className="text-2xs leading-relaxed text-muted-foreground">
          {t('manualReview.slip.uploadHint')}
        </p>
      </CardContent>
    </Card>
  );
}
